import React, { useState, useEffect } from 'react';
import schema from './schema';
import defaultData from './defaultData';
import validator from './validator';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiAlertTriangle, FiPrinter, FiSave, FiZap } = FiIcons;

const ChpCommissioningServicingRecordForm = ({ formInstanceId, onSave, onGeneratePDF }) => {
  const [formData, setFormData] = useState(defaultData);
  const [validation, setValidation] = useState({ isValid: true, errors: {}, warnings: {} });
  const [activeTab, setActiveTab] = useState('contractorDetails');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const result = validator.validate(formData);
    setValidation(result);
  }, [formData]);

  const handleChange = (sectionId, fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [fieldId]: value
      }
    }));
  };

  const handleSave = () => {
    setIsSaving(true);
    if (onSave) onSave(formData);
    setTimeout(() => setIsSaving(false), 500);
  };

  const currentSection = schema.sections.find(s => s.id === activeTab) || schema.sections[0];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-950 via-stone-900 to-amber-900 text-white p-6 rounded-xl shadow-lg mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <span className="bg-amber-500/30 text-amber-200 text-xs font-semibold px-2.5 py-0.5 rounded border border-amber-400/30">
            ENA EREC G99 • Commercial Cogeneration
          </span>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Combined Heat & Power (CHP) Record</h1>
          <p className="text-amber-200 text-sm mt-1">Plant Room Cogeneration Commissioning, Servicing & G99 Grid Interface</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            type="button"
            onClick={() => onGeneratePDF && onGeneratePDF(formData)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
          >
            <SafeIcon icon={FiPrinter} className="w-4 h-4" />
            Generate PDF
          </button>
        </div>
      </div>

      {/* Output HUD */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Electrical Output</div>
          <div className="text-2xl font-bold text-amber-400 mt-1">
            {formData.chpSpecification?.electricalOutputKwe || 0} <span className="text-sm font-normal text-slate-300">kWe</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">G99 Relay: {formData.electricalGridProtection?.g99ProtectionRelayTested ? 'Verified' : 'Pending'}</div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Thermal Heat Output</div>
          <div className="text-2xl font-bold text-rose-400 mt-1">
            {formData.chpSpecification?.thermalOutputKwt || 0} <span className="text-sm font-normal text-slate-300">kWt</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">Fuel: {formData.chpSpecification?.fuelType}</div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Flue CO Emissions</div>
          <div className="text-2xl font-bold text-cyan-400 mt-1">
            {formData.gasAndCombustion?.flueCoPpm || 0} <span className="text-sm font-normal text-slate-300">ppm</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">Exhaust Temp: {formData.gasAndCombustion?.flueGasTempC || 0}°C</div>
        </div>

        <div className="bg-slate-800 text-white p-4 rounded-xl border border-slate-700">
          <div className="text-xs text-slate-400 uppercase font-semibold">Engine Hours</div>
          <div className="text-2xl font-bold text-emerald-400 mt-1">
            {formData.chpSpecification?.operatingHoursTotal || 0} <span className="text-sm font-normal text-slate-300">hrs</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">Status: {formData.declarations?.overallStatus}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-2 border-b border-slate-200 dark:border-slate-800 pb-3 mb-6 scrollbar-thin">
        {schema.sections.map(section => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveTab(section.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === section.id
                ? 'bg-amber-600 text-white shadow'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
            }`}
          >
            {section.title}
          </button>
        ))}
      </div>

      {/* Fields */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm mb-6">
        <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-4 pb-2 border-b border-slate-200 dark:border-slate-800">
          {currentSection.title}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentSection.fields.map(field => {
            const fieldKey = `${currentSection.id}.${field.id}`;
            const error = validation.errors[fieldKey];
            const warning = validation.warnings[fieldKey];
            const value = formData[currentSection.id]?.[field.id] ?? '';

            return (
              <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>

                {field.type === 'select' ? (
                  <select
                    value={value}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                  >
                    {field.options?.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={value}
                    rows={3}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                  />
                ) : field.type === 'signature' ? (
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-4 text-center">
                    <input
                      type="text"
                      placeholder="Type engineer signature name"
                      value={value}
                      onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-2"
                    />
                  </div>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                    step={field.type === 'number' ? 'any' : undefined}
                    value={value}
                    onChange={(e) => handleChange(currentSection.id, field.id, e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                  />
                )}

                {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
                {warning && <p className="text-xs text-amber-500 mt-1">{warning}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChpCommissioningServicingRecordForm;
